import { DownOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Form, InputNumber, Tooltip } from 'antd';
import { ToggleHeader, ToggleSwitch } from '@/components/toggle-switch';
import { cn } from '@/libs/utils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';

/** E-invoicing switch and its settings block. */
export default function CompanyEInvoiceSettings() {
  const {
    advancedSalesInvoicingEnabledKeys,
    advancedSalesInvoicingEnabledState,
    autoArchiveAfterMonthsKeys,
    autoArchiveAfterMonthsState,
    autoArchiveInvoicesEnabledKeys,
    autoArchiveInvoicesEnabledState,
    autoArchiveInvoicesOn,
    eInvoiceChecked,
    eInvoiceExpanded,
    eInvoiceKeys,
    eInvoiceState,
    form,
    invoiceJournalEnabledKeys,
    invoiceJournalEnabledState,
    invoiceProjectEnabledKeys,
    invoiceProjectEnabledState,
    isProfile,
    manualArchiveInvoicesEnabledKeys,
    manualArchiveInvoicesEnabledState,
    manualPurchaseInvoiceEnabledKeys,
    manualPurchaseInvoiceEnabledState,
    postedToErpTrackingEnabledKeys,
    postedToErpTrackingEnabledState,
    qrInEInvoicesKeys,
    qrInEInvoicesState,
    salesJournalEnabledKeys,
    salesJournalEnabledState,
    sectionClass,
    setEInvoiceExpanded,
    showInternalRefNumberKeys,
    showInternalRefNumberState,
    t,
    taxReturnsClientViewKeys,
    taxReturnsClientViewState,
    ledgerCardsEnabledKeys,
    ledgerCardsEnabledState,
    vatNoticeEnabledKeys,
    vatNoticeEnabledState,
  } = useCompanyGeneralDataForm();
  // On the profile: laid out by CompanyEInvoiceProfilePanel.
  if (isProfile) return null;

  return (
    <>
      {eInvoiceState?.visible && (
        <div className={sectionClass('efakture')}>
          {!isProfile && <hr className="my-5" />}

          <ToggleHeader
            title={t('eInvoicePlatform', { ns: 'forms' })}
            className={cn(
              '',
              eInvoiceChecked && eInvoiceExpanded ? 'mb-5' : ''
            )}
          >
            <div className="flex items-center gap-3">
              {eInvoiceChecked && (
                <DownOutlined
                  className={cn(
                    'cursor-pointer text-xs text-gray-400 transition-transform',
                    !eInvoiceExpanded && '-rotate-90'
                  )}
                  onClick={() => setEInvoiceExpanded((v) => !v)}
                />
              )}
              <ToggleSwitch
                name={eInvoiceKeys}
                form={form}
                disabled={eInvoiceState?.disabled}
                onChangeAction={(val) => val && setEInvoiceExpanded(true)}
              />
            </div>
          </ToggleHeader>

          <div
            className={cn(
              eInvoiceChecked && eInvoiceExpanded
                ? 'block rounded-md border bg-white p-4'
                : 'hidden'
            )}
          >
            {qrInEInvoicesState?.visible && (
              <ToggleHeader
                title={t('qrInEInvoices', { ns: 'forms' })}
                className="mb-5"
              >
                <ToggleSwitch
                  name={qrInEInvoicesKeys}
                  form={form}
                  disabled={qrInEInvoicesState?.disabled}
                />
              </ToggleHeader>
            )}

            {invoiceProjectEnabledState?.visible && (
              <ToggleHeader title={t('invoiceProjectEnabled', { ns: 'forms' })}>
                <ToggleSwitch
                  name={invoiceProjectEnabledKeys}
                  form={form}
                  disabled={invoiceProjectEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {manualPurchaseInvoiceEnabledState?.visible && (
              <ToggleHeader
                title={t('manualPurchaseInvoiceEnabled', { ns: 'forms' })}
              >
                <ToggleSwitch
                  name={manualPurchaseInvoiceEnabledKeys}
                  form={form}
                  disabled={manualPurchaseInvoiceEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {invoiceJournalEnabledState?.visible && (
              <ToggleHeader title={t('invoiceJournalEnabled', { ns: 'forms' })}>
                <ToggleSwitch
                  name={invoiceJournalEnabledKeys}
                  form={form}
                  disabled={invoiceJournalEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {salesJournalEnabledState?.visible && (
              <ToggleHeader title={t('salesJournalEnabled', { ns: 'forms' })}>
                <ToggleSwitch
                  name={salesJournalEnabledKeys}
                  form={form}
                  disabled={salesJournalEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {vatNoticeEnabledState?.visible && (
              <ToggleHeader title={t('vatNoticeEnabled', { ns: 'forms' })}>
                <ToggleSwitch
                  name={vatNoticeEnabledKeys}
                  form={form}
                  disabled={vatNoticeEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {advancedSalesInvoicingEnabledState?.visible && (
              <ToggleHeader
                title={t('advancedSalesInvoicingEnabled', { ns: 'forms' })}
              >
                <ToggleSwitch
                  name={advancedSalesInvoicingEnabledKeys}
                  form={form}
                  disabled={advancedSalesInvoicingEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {autoArchiveInvoicesEnabledState?.visible && (
              <ToggleHeader
                title={t('autoArchiveInvoicesEnabled', { ns: 'forms' })}
              >
                <ToggleSwitch
                  name={autoArchiveInvoicesEnabledKeys}
                  form={form}
                  disabled={autoArchiveInvoicesEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {autoArchiveInvoicesEnabledState?.visible &&
              autoArchiveInvoicesOn &&
              autoArchiveAfterMonthsState?.visible && (
                <Form.Item
                  name={autoArchiveAfterMonthsKeys}
                  label={t('autoArchiveAfterMonths', { ns: 'forms' })}
                  tooltip={t('autoArchiveAfterMonthsTooltip', {
                    ns: 'forms',
                  })}
                  rules={[
                    {
                      type: 'integer',
                      min: 1,
                      max: 24,
                      message: t('autoArchiveAfterMonthsRange', {
                        ns: 'forms',
                      }),
                    },
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={24}
                    precision={0}
                    style={{ width: '100%' }}
                    disabled={autoArchiveAfterMonthsState?.disabled}
                  />
                </Form.Item>
              )}

            {manualArchiveInvoicesEnabledState?.visible && (
              <ToggleHeader
                title={t('manualArchiveInvoicesEnabled', { ns: 'forms' })}
              >
                <ToggleSwitch
                  name={manualArchiveInvoicesEnabledKeys}
                  form={form}
                  disabled={manualArchiveInvoicesEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {postedToErpTrackingEnabledState?.visible && (
              <ToggleHeader
                title={t('postedToErpTrackingEnabled', { ns: 'forms' })}
              >
                <ToggleSwitch
                  name={postedToErpTrackingEnabledKeys}
                  form={form}
                  disabled={postedToErpTrackingEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {taxReturnsClientViewState?.visible && (
              <ToggleHeader title={t('companySwitch', { ns: 'taxReturns' })}>
                <ToggleSwitch
                  name={taxReturnsClientViewKeys}
                  form={form}
                  disabled={taxReturnsClientViewState?.disabled}
                />
              </ToggleHeader>
            )}

            {ledgerCardsEnabledState?.visible && (
              <ToggleHeader
                title={
                  <Tooltip title={t('ledgerCardsEnabledTooltip', { ns: 'forms' })}>
                    <span>
                      {t('ledgerCardsEnabled', { ns: 'forms' })}{' '}
                      <QuestionCircleOutlined className="opacity-60" />
                    </span>
                  </Tooltip>
                }
              >
                <ToggleSwitch
                  name={ledgerCardsEnabledKeys}
                  form={form}
                  disabled={ledgerCardsEnabledState?.disabled}
                />
              </ToggleHeader>
            )}

            {showInternalRefNumberState?.visible && (
              <ToggleHeader title={t('showInternalRefNumber', { ns: 'forms' })}>
                <ToggleSwitch
                  name={showInternalRefNumberKeys}
                  form={form}
                  disabled={showInternalRefNumberState?.disabled}
                />
              </ToggleHeader>
            )}
          </div>
        </div>
      )}
    </>
  );
}
